import { startOfDay } from "date-fns";
import {
  fanOutHours,
  getISODay,
  normalizeToSlotStart,
  SLOT_PRIORITY,
  TIME_SLOTS,
  type SlotEntry,
} from "../../../utils/publicationDistribution";
import { applyNaturalDrift } from "./drift";
import { isFailedPub, isImmutablePub } from "./status";
import type { AccountCadence } from "./types";
import { DEFAULT_ACCOUNT_CADENCE, getPreferredHoursForCadence } from "./types";

export function pubKey(videoId: string, accountId: string): string {
  return `${videoId}:${accountId}`;
}

function countOccupiedOnDay(dayStart: Date, allOccupied: Set<number>): number {
  let count = 0;
  for (let hour = 0; hour < 24; hour++) {
    const t = new Date(dayStart);
    t.setHours(hour, 0, 0, 0);
    if (allOccupied.has(t.getTime())) count++;
  }
  return count;
}

export function nextAvailableSlot(
  cadence: AccountCadence,
  from: Date,
  occupied: Set<number>,
  crossProject: Set<number>,
  preferredHours?: number[],
): Date {
  const merged = new Set<number>();
  occupied.forEach((v) => merged.add(v));
  crossProject.forEach((v) => merged.add(v));

  const now = new Date();
  const effectiveFrom = from < now ? now : from;
  const maxPerDay = cadence.maxPostsPerDay;
  const allowedSet = new Set(cadence.allowedISODays);

  let config: SlotEntry[] = SLOT_PRIORITY;

  if (preferredHours && preferredHours.length > 0) {
    // Explicit session hours, chronological: an account posting k times a
    // day consumes the earliest k, and partial days fill earliest-first.
    // Sorting must precede the cadence slice — "earliest k of the day's
    // hours", unlike the window derivation where the slice honors priority
    // order (getSessionPreferredHours). Single-hour bands (no fan-out) —
    // an occupied or elapsed hour flows to the next allowed day's same
    // hour instead of borrowing a neighbor hour. preferredTimeSlotId is
    // obsolete on this path.
    config = getPreferredHoursForCadence(
      [...preferredHours].sort((a, b) => a - b),
      maxPerDay,
    ).map((hour) => ({ startHour: hour, endHour: hour, preferredHour: hour }));
  } else if (cadence.preferredTimeSlotId) {
    const slotDef = TIME_SLOTS[cadence.preferredTimeSlotId];
    if (slotDef && !slotDef.disabled) {
      config = config.filter(
        (e) => e.startHour === slotDef.startHour && e.endHour === slotDef.endHour,
      );
    }
  }

  function buildSearchDays(windowStart: Date, windowDays: number): Date[] {
    const days: Date[] = [];
    let cursor = startOfDay(windowStart);
    for (let i = 0; i < windowDays; i++) {
      const isoDay = getISODay(cursor);
      if (allowedSet.has(isoDay)) {
        days.push(new Date(cursor));
      }
      cursor = new Date(cursor);
      cursor.setDate(cursor.getDate() + 1);
      cursor.setHours(0, 0, 0, 0);
    }
    return days;
  }

  function tryCandidate(day: Date, hour: number): Date | null {
    const candidate = new Date(day);
    candidate.setHours(hour, 0, 0, 0);
    if (candidate < effectiveFrom) return null;
    if (candidate <= now) return null;
    if (merged.has(candidate.getTime())) return null;
    if (countOccupiedOnDay(startOfDay(candidate), merged) >= maxPerDay) return null;
    return candidate;
  }

  function isPastHour(day: Date, hour: number): boolean {
    const candidate = new Date(day);
    candidate.setHours(hour, 0, 0, 0);
    return candidate < effectiveFrom || candidate <= now;
  }

  function fallbackScan(searchDays: Date[]): Date | null {
    for (const day of searchDays) {
      for (let hour = 6; hour <= 23; hour++) {
        const result = tryCandidate(day, hour);
        if (result) return result;
      }
    }
    return null;
  }

  if (maxPerDay === 1) {
    // Mode 1: hour→days — keeps posts at the optimal hour across different days
    for (let windowOffset = 0; windowOffset < 365; windowOffset += 14) {
      const windowStart = new Date(effectiveFrom);
      windowStart.setDate(windowStart.getDate() + windowOffset);
      const searchDays = buildSearchDays(
        windowOffset === 0 ? effectiveFrom : startOfDay(windowStart),
        14,
      );
      if (searchDays.length === 0) continue;

      for (const entry of config) {
        const hours = fanOutHours(entry.preferredHour, entry.startHour, entry.endHour);

        for (const hour of hours) {
          for (const day of searchDays) {
            const result = tryCandidate(day, hour);
            if (result) return result;
          }
        }
      }

      const fb = fallbackScan(searchDays);
      if (fb) return fb;
    }
  } else {
    // Mode 2: day→slots — same steady-state bands every day (rhythm with a
    // cap, not a quota): only the top maxPerDay priority bands are used, so
    // every day shares the same slot times and a partial first day simply
    // gets fewer posts.
    const steadyStateConfig = config.slice(0, maxPerDay);

    // Fan-out within a band is a capacity mechanism: it is only needed when
    // fewer bands than maxPerDay are available (preferred-slot lock), so a
    // band must host several posts per day. With one band per post, an
    // occupied preferred hour flows to the next day's same slot instead.
    const allowFanOut = steadyStateConfig.length < maxPerDay;

    for (let windowOffset = 0; windowOffset < 365; windowOffset += 14) {
      const windowStart = new Date(effectiveFrom);
      windowStart.setDate(windowStart.getDate() + windowOffset);
      const searchDays = buildSearchDays(
        windowOffset === 0 ? effectiveFrom : startOfDay(windowStart),
        14,
      );
      if (searchDays.length === 0) continue;

      for (const day of searchDays) {
        if (countOccupiedOnDay(startOfDay(day), merged) >= maxPerDay) continue;

        // A band whose preferred hour already passed is skipped for this day
        // (the post flows to the next day) — fan-out is reserved for
        // occupancy conflicts, not for compensating elapsed time.
        const entriesAhead = steadyStateConfig.filter(
          (entry) => !isPastHour(day, entry.preferredHour),
        );

        // First pass: try only the preferred hour from each entry
        for (const entry of entriesAhead) {
          const result = tryCandidate(day, entry.preferredHour);
          if (result) return result;
        }

        // Second pass: fan-out non-preferred hours within each entry
        if (allowFanOut) {
          for (const entry of entriesAhead) {
            const hours = fanOutHours(entry.preferredHour, entry.startHour, entry.endHour);
            for (const hour of hours) {
              if (hour === entry.preferredHour) continue;
              const result = tryCandidate(day, hour);
              if (result) return result;
            }
          }
        }
      }
    }
  }

  const fallback = new Date(effectiveFrom);
  fallback.setHours(fallback.getHours() + 1, 0, 0, 0);
  return fallback;
}

export type DistributeParams = {
  orderedVideoIds: string[];
  effectiveAccountsByVideoId: Record<string, string[]>;
  perVideoState: Record<
    string,
    {
      localPublications: {
        accountId: string;
        status: string;
        scheduledAt: Date | null;
        id: string;
      }[];
    }
  >;
  accountCadences: Map<string, AccountCadence>;
  startFrom: Date;
  crossProjectOccupied: Map<string, Set<number>>;
  existingPublicationIds: Set<string>;
  freezePublishedOnly: boolean;
  customDatetimeKeys?: Set<string>;
  /** §5.1 saved posts (computeSavedKeys) — anchored under every command,
   *  frozen or not, unless `replan` names them. Always passed by the
   *  reducer; data, like customDatetimeKeys, so the distribution stays
   *  testable without a session. */
  anchoredKeys?: Set<string>;
  /** §11 re-plan: the saved keys to un-anchor, each starting from its own
   *  account's bound (`startByAccount`, falling back to `startFrom`). A
   *  key with a live custom pin stays pinned. Every other key keeps the
   *  session bound and its anchoring. `startByKey` bounds one key on its
   *  own (§13.6 queue move: the slot of the rank it was dropped into) and
   *  wins over the account bound; `keepOrder` makes the walk keep re-laid
   *  keys of one account in queue order — each starts no earlier than
   *  where the previous re-laid key of that account landed. */
  replan?: {
    keys: Set<string>;
    startByAccount: Map<string, Date>;
    startByKey?: Map<string, Date>;
    keepOrder?: boolean;
  };
  preferredHours?: number[];
};

export function distributeAllPublications(
  params: DistributeParams,
): Map<string, Date> {
  const {
    orderedVideoIds,
    effectiveAccountsByVideoId,
    perVideoState,
    accountCadences,
    startFrom,
    crossProjectOccupied,
    existingPublicationIds,
    freezePublishedOnly,
    customDatetimeKeys,
    anchoredKeys,
    replan,
    preferredHours,
  } = params;

  const result = new Map<string, Date>();
  const duplicateKeys = new Set<string>();
  for (const videoId of orderedVideoIds) {
    const seen = new Set<string>();
    for (const pub of perVideoState[videoId]?.localPublications ?? []) {
      if (seen.has(pub.accountId)) duplicateKeys.add(pubKey(videoId, pub.accountId));
      seen.add(pub.accountId);
    }
  }

  function isAnchored(
    videoId: string,
    pub: {
      id: string;
      status: string;
      scheduledAt: Date | null;
      accountId: string;
    },
  ): boolean {
    // Every in-flight status anchors, not only published: a publishing
    // post's hour must read as occupied or a draft lands in it.
    if (isImmutablePub(pub) || isFailedPub(pub)) return true;
    const key = pubKey(videoId, pub.accountId);
    // One account-keyed map entry cannot represent a replan of multiple
    // legacy posts. Keep every such row anchored and reserve all its slots.
    if (duplicateKeys.has(key)) return true;
    if (replan?.keys.has(key) && !customDatetimeKeys?.has(key)) return false;
    if (anchoredKeys?.has(key)) return true;
    if (customDatetimeKeys?.has(key)) return true;
    if (freezePublishedOnly) return false;
    return existingPublicationIds.has(pub.id) && pub.scheduledAt != null;
  }

  const accountOccupied = new Map<string, Set<number>>();

  function getOccupied(accountId: string): Set<number> {
    let set = accountOccupied.get(accountId);
    if (!set) {
      set = new Set<number>();
      const cross = crossProjectOccupied.get(accountId);
      if (cross) {
        cross.forEach((ts) => set!.add(ts));
      }
      accountOccupied.set(accountId, set);
    }
    return set;
  }

  for (const videoId of orderedVideoIds) {
    const accounts = new Set(effectiveAccountsByVideoId[videoId] ?? []);
    const vs = perVideoState[videoId];
    if (!vs) continue;

    for (const pub of vs.localPublications) {
      const accountId = pub.accountId;
      if (!accounts.has(accountId)) continue;
      if (!isAnchored(videoId, pub)) continue;
      if (pub.scheduledAt == null) continue;

      const occupied = getOccupied(accountId);
      const ts = normalizeToSlotStart(new Date(pub.scheduledAt)).getTime();
      occupied.add(ts);
      const key = pubKey(videoId, accountId);
      if (!result.has(key)) result.set(key, new Date(pub.scheduledAt));
    }
  }

  const latestPublishedByAccount = new Map<string, Date>();
  for (const videoId of orderedVideoIds) {
    const vs = perVideoState[videoId];
    if (!vs) continue;
    for (const pub of vs.localPublications) {
      if (pub.status !== "published" || pub.scheduledAt == null) continue;
      const d = new Date(pub.scheduledAt);
      const existing = latestPublishedByAccount.get(pub.accountId);
      if (!existing || d.getTime() > existing.getTime()) {
        latestPublishedByAccount.set(pub.accountId, d);
      }
    }
  }

  const emptyCross = new Set<number>();
  // §13.6 keepOrder: where the previous re-laid key of each account landed.
  const lastReplanLandedByAccount = new Map<string, Date>();

  for (const videoId of orderedVideoIds) {
    const accounts = effectiveAccountsByVideoId[videoId] ?? [];
    const vs = perVideoState[videoId];
    if (!vs) continue;

    for (const accountId of accounts) {
      const key = pubKey(videoId, accountId);
      if (result.has(key)) continue;

      const cadence = accountCadences.get(accountId) ?? {
        ...DEFAULT_ACCOUNT_CADENCE,
      };
      const occupied = getOccupied(accountId);

      // A re-planned key starts from its own bound — its key's, else its
      // account's (its earliest saved date) — when that is later than the
      // session bound, and under keepOrder no earlier than the previous
      // re-laid key of its account; everything else keeps the session
      // bound.
      const isReplanKey = replan?.keys.has(key) ?? false;
      let from = startFrom;
      if (isReplanKey && replan) {
        const bounds = [
          replan.startByKey?.get(key) ?? replan.startByAccount.get(accountId),
          replan.keepOrder ? lastReplanLandedByAccount.get(accountId) : undefined,
        ];
        for (const bound of bounds) {
          if (bound && bound.getTime() > from.getTime()) from = bound;
        }
      }
      const latestPublished = latestPublishedByAccount.get(accountId);
      const effectiveFrom =
        latestPublished && latestPublished.getTime() > from.getTime()
          ? latestPublished
          : from;

      const slot = nextAvailableSlot(
        cadence,
        effectiveFrom,
        occupied,
        emptyCross,
        preferredHours,
      );
      // Natural-hours drift is the default engine: freshly assigned slots
      // land a few deterministic minutes past the hour (never anchored or
      // custom datetimes, which are frozen above). Seeded by video so the
      // same video posts at the same minute across platforms.
      result.set(key, applyNaturalDrift(slot, videoId));
      occupied.add(normalizeToSlotStart(slot).getTime());
      if (isReplanKey) lastReplanLandedByAccount.set(accountId, slot);
    }
  }

  return result;
}
